<template>
  <div>
    <div class="flex items-center justify-between m-2 mb-4">
      <div class="flex items-center gap-4">
        <div class="relative">
          <contact-avatar
            :contact="member"
            :contact-type="member.type"
          />
          <contact-status
            v-if="member.imLogin"
            :p-served="member.imLogin"
            compact-mode
          />
        </div>
        <div class="flex flex-col justify-between py-1 min-w-0">
          <name-with-user-agent
            class="text-black-200 text-label-r-14-16"
            :name="member.name"
            :user-agent="participantUserAgents"
          />
          <div
            v-if="statusLabel"
            class="text-1316 text-rackley-shades-600 mt-2"
          >
            {{ statusLabel }}
          </div>
        </div>
      </div>
      <wui-btn
        data-test="close"
        class="text-body-2-black-tints-400!"
        prepend-icon="closeRegularMdM"
        icon
        :size="32"
        @click="emit('close')"
      />
    </div>
    <div class="border-t border-t-rackley-shades-600">
      <div class="p-4 text-label-r-13-16 ">
        <div v-if="mainDisplayNumber">
          <div class="flex items-center gap-x-2">
            <p class="text-[#71889D]">
              {{ $t('chat.phoneNumber') }}:
            </p>
            <p class="text-black-200">
              {{ mainDisplayNumber }}
            </p>
          </div>
          <div
            v-if="member.mobilePhone && member.mobilePhone !== mainDisplayNumber"
            class="flex items-center gap-x-2"
          >
            <p class="text-[#71889D]">
              {{ $t('chat.mobNumber') }}:
            </p>
            <p class="text-black-200">
              {{ member.mobilePhone }}
            </p>
          </div>
          <div
            v-if="member.email"
            class="flex items-center gap-x-2"
          >
            <p class="text-[#71889D]">
              {{ $t('common.emailA') }}:
            </p>
            <p class="text-black-200">
              <a
                :href="`mailto:${member.email}`"
                target="_blank"
              >
                {{ member.email }}
                <wui-icon
                  name="linkM"
                  small
                  class="text-fg-base-fg3"
                />
              </a>
            </p>
          </div>
          <div
            v-if="member.organization"
            class="flex items-center gap-x-2"
          >
            <p class="text-[#71889D]">
              {{ $t('common.organization') }}
            </p>
            <p class="text-black-200">
              {{ member.organization }}
            </p>
          </div>
          <div
            v-if="member.organizationalUnit"
            class="flex items-center gap-x-2"
          >
            <p class="text-[#71889D]">
              {{ $t('common.department') }}
            </p>
            <p class="text-black-200">
              {{ member.organizationalUnit }}
            </p>
          </div>
          <div
            v-if="member.position"
            class="flex items-center gap-x-2"
          >
            <p class="text-[#71889D]">
              {{ $t('common.position') }}
            </p>
            <p class="text-black-200">
              {{ member.position }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn, WuiIcon } from '@wui/common-library'
import { computed, onMounted, onUnmounted, watch } from 'vue'


import { useStoreConferenceMediaStatuses } from '@/entities/conference-participant'
import { ContactAvatar } from '@/entities/contact-avatar'
import { MemberStatus } from '@/entities/room'

import { NameWithUserAgent } from '@/shared/components/user-agent-icon'
import { useStatusSubscribe } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { ContactStatus } from '@/shared/contact-status'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'

const props = defineProps<{
  member: Contact
  status: MemberStatus
  room?: Room
  participantIdForStatus?: string
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { t } = useLocalization()
const { getConferenceCallsByImLogin } = useStoreConferenceMediaStatuses()

const participantUserAgents = computed(() => {
  if (!props.room) {
    return []
  }

  const id = props.participantIdForStatus ?? props.member.imLogin
  if (!id) {
    return []
  }

  return [...new Set(
    getConferenceCallsByImLogin(props.room, id)
      .map(participant => participant.userAgent)
      .filter(Boolean),
  )]
})

const mainDisplayNumber = computed(() =>
  props.member.internalNumber || props.member.mobilePhone || props.participantIdForStatus || '',
)

const statusLabel = computed<string>(() => {
  if (props.status === MemberStatus.Active) {
    return t('calls.inConference')
  }

  if (props.status === MemberStatus.Dialing) {
    return t('calls.joins')
  }

  if (props.status === MemberStatus.OnHold) {
    return t('common.isOnHold')
  }

  return ''
})

const { subscribe, unsubscribe } = useStatusSubscribe()

onMounted(() => {
  if (props.member.imLogin) {
    subscribe(props.member.imLogin)
  }
})

onUnmounted(() => {
  if (props.member.imLogin) {
    unsubscribe(props.member.imLogin)
  }
})

watch(() => props.member.imLogin, (newValue, oldValue) => {
  if (newValue) {
    subscribe(newValue)
  } else if (oldValue) {
    unsubscribe(oldValue)
  }
})
</script>
